import { NavLink } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { Home, Clapperboard, MonitorPlay, Workflow } from 'lucide-react';
import { Sidebar as UiSidebar, SidebarLink } from '@parrot/ui';
import { LOGO } from '../../lib/cdn';
import { ThemeToggle } from '../ThemeToggle/ThemeToggle';
import styles from './Sidebar.module.css';

/* Icons name the entity the page is about, not the page's shape: a leaderboard
   of titles is still titles. Clapperboard (content) and MonitorPlay (streaming)
   read as a pair; Workflow matches its label rather than promising AI magic. */
const NAV_ITEMS = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/titles/leaderboard', icon: Clapperboard, label: 'Titles' },
  { to: '/platforms/leaderboard', icon: MonitorPlay, label: 'Platforms' },
  { to: '/workflows', icon: Workflow, label: 'Workflows' },
];

interface SidebarProps {
  pinned: boolean;
  onPinnedChange: (pinned: boolean) => void;
}

/* The shared Sidebar's `logo` slot wants ONE node per slot — it styles its
   direct img child and owns the collapse/expand (mark vs full) swap. So we pass
   a SINGLE <img> per slot and swap the theme at the `src`, reactively.

   `data-theme` lives on <html>; subscribe to attribute changes so the logo
   flips the instant the ThemeToggle writes it, without a reload. */
function useIsLight(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const obs = new MutationObserver(onChange);
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
      return () => obs.disconnect();
    },
    () => document.documentElement.getAttribute('data-theme') === 'light',
    () => false,
  );
}

const LogoMark = () => {
  const light = useIsLight();
  return (
    <img
      className={styles.logo}
      src={light ? LOGO.logomarkLightBg : LOGO.logomarkDarkBg}
      alt={'Streaming Economics'}
    />
  );
};

const LogoFull = () => {
  const light = useIsLight();
  return (
    <img
      className={styles.logo}
      src={light ? LOGO.fullLightBg : LOGO.fullDarkBg}
      alt={'Streaming Economics'}
    />
  );
};

/* Theme toggle lives in the sidebar footer, mirroring location-intelligence.
   The rail doesn't expose its hover-expand state as a prop, so we render both
   variants and let CSS show the compact icon on the collapsed rail and the full
   switch once expanded — keyed off [data-expanded] on the nav root. */
const Footer = () => (
  <div className={styles.themeToggleWrap}>
    <ThemeToggle variant={'icon'} className={styles.toggleCollapsed} />
    <ThemeToggle variant={'switch'} className={styles.toggleExpanded} />
  </div>
);

export function Sidebar({ pinned, onPinnedChange }: SidebarProps) {
  return (
    <UiSidebar
      className={`app-chrome ${styles.sidebar}`}
      pinned={pinned}
      onPinnedChange={onPinnedChange}
      logo={{ mark: <LogoMark />, full: <LogoFull /> }}
      logoAs={NavLink}
      logoProps={{ to: '/' }}
      footer={<Footer />}
    >
      {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
        <SidebarLink
          key={to}
          as={NavLink}
          to={to}
          end={to === '/'}
          icon={<Icon size={20} strokeWidth={1.75} />}
        >
          {label}
        </SidebarLink>
      ))}
    </UiSidebar>
  );
}
